import {
  FetchPolicy,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useApolloClient,
  useQuery,
} from "@apollo/client";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "@recare/core/consts";
import { useActivityContext } from "@recare/core/model/utils/browser/ActivitySingleton";
import {
  ApolloEncryptionContext,
  QueryProgress,
  ValueOf,
} from "@recare/core/types";
import { TypedDocumentNode as DocumentNode } from "apollo/types";
import { logError } from "apollo/utils";
import { useCallback, useEffect, useState } from "react";
import { getFetchPolicy, useApolloProgress } from "../utils";

export type CustomQueryHook<
  DocumentType,
  ExtraProps extends object | undefined = undefined,
> = ExtraProps extends object
  ? (
      props: ExtraProps &
        Omit<Partial<CustomQueryHookOptions<DocumentType>>, keyof ExtraProps>,
    ) => CustomQueryHookResult<DocumentType>
  : (
      props?: Partial<CustomQueryHookOptions<DocumentType>>,
    ) => CustomQueryHookResult<DocumentType>;

type CustomQueryHookResult<DocumentType> = DocumentType extends DocumentNode<
  infer Result,
  infer Variables
>
  ? [
      QueryProgress,
      ValueOf<Result>,
      Omit<
        QueryResult<
          Result,
          Variables extends OperationVariables ? Variables : never
        >,
        "data"
      >,
    ]
  : never;

type CustomQueryHookOptions<DocumentType> = DocumentType extends DocumentNode<
  infer Result,
  infer Variables
>
  ? QueryHookOptions<
      Result,
      Variables extends OperationVariables ? Variables : never
    > &
      Variables
  : never;

type TypedGenericQueryHookResult<
  TData,
  TVariables extends OperationVariables = OperationVariables,
> = [
  QueryProgress,
  QueryResult<TData, TVariables>["data"],
  Omit<QueryResult<TData, TVariables>, "data">,
];

export function getQueryProgress(
  loading: boolean,
  error: any,
  skip?: boolean,
): QueryProgress {
  if (loading) return QUERY_PROGRESS_PENDING;
  if (error) {
    logError(error);
    return QUERY_PROGRESS_FAILED;
  }
  if (skip) {
    return QUERY_PROGRESS_NOT_STARTED;
  }
  return QUERY_PROGRESS_SUCCEED;
}

export const usePolling = ({
  shouldPoll = true,
  startPolling,
  stopPolling,
  interval = 2 * 60 * 1000,
  dependencies = [],
}: {
  dependencies?: Array<any>;
  interval?: number;
  shouldPoll?: boolean;
  startPolling: (arg0: number) => void;
  stopPolling: () => void;
}) => {
  // control if the query ever started polling
  const [isPolling, setIsPolling] = useState(false);
  const { active } = useActivityContext();

  // init
  useEffect(() => {
    // start
    if (shouldPoll) {
      startPolling(interval);
      setIsPolling(true);
      // stop
    } else if (isPolling) {
      stopPolling();
      setIsPolling(false);
    }
  }, [shouldPoll]);

  // re-poll with dependecies that affect the query
  useEffect(() => {
    if (isPolling) {
      stopPolling();
      setTimeout(() => {
        startPolling(interval);
      }, 0);
    }
  }, [...dependencies]);

  // check for browser activity
  useEffect(() => {
    // stop polling if user is inactive
    if (!active && isPolling) {
      stopPolling();
      // restart polling if user is active
    } else if (active && !isPolling && shouldPoll) {
      setTimeout(() => {
        startPolling(interval);
      }, 0);
    }
  }, [active]);
};

export function useTypedGenericQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): TypedGenericQueryHookResult<TData, TVariables> {
  const result = useQuery(query, {
    ...options,
    fetchPolicy: getFetchPolicy(options?.fetchPolicy),
  });
  const { progress } = useApolloProgress(result);
  const { data, ...rest } = result;
  return [progress, data, rest];
}

export function useLazyQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  options?: {
    encryptionContext?: ApolloEncryptionContext;
    fetchPolicy?: FetchPolicy;
    variables?: TVariables;
  },
) {
  const client = useApolloClient();
  return useCallback(
    (variables?: TVariables) =>
      client
        .query<{ [queryName: string]: TData }, any>({
          query: query,
          variables:
            options?.variables || variables
              ? ({
                  ...(options?.variables ?? {}),
                  ...(variables ?? {}),
                } as TVariables)
              : undefined,
          fetchPolicy: options?.fetchPolicy ?? "network-only",
          context: { encryptionContext: options?.encryptionContext },
        })
        .then((response) => {
          const queryProgress = getQueryProgress(
            response?.loading,
            response?.errors,
          );
          return { ...response, queryProgress };
        }),
    [client, options],
  );
}
